import Color from "color";
import {
  getColorsForDataset,
  type DataPeriod,
  type Data,
} from "@bagel-web/components";
import { CommonAgentSummary, SessionStats } from "../../types";
import { util } from "../..";
import { getFriendlyAgentName } from "../../util";

const getErrorColors = (): {
  borderColor: string;
  backgroundColor: string;
  borderWidth: number;
  borderSkipped: "middle";
} => {
  const baseColor = "red";
  return {
    borderColor: Color(baseColor).alpha(1.0).hexa(),
    backgroundColor: Color(baseColor).alpha(0.5).hexa(),
    borderWidth: 2,
    borderSkipped: "middle",
  };
};

export const getSessionHistogramDataByAgent = ({
  agents,
  sessionStats,
  isDarkMode,
}: {
  agents: Array<CommonAgentSummary>;
  sessionStats?: SessionStats;
  isDarkMode: boolean;
}) => {
  const data: Data = {
    labels: [],
    datasets: [],
  };

  if (
    sessionStats?.agent_stats &&
    sessionStats.end?.time &&
    sessionStats.start?.time
  ) {
    Object.entries(sessionStats.agent_stats).forEach(
      ([agentId, agentStats], agentIndex) => {
        const agentData: Array<DataPeriod> = [];
        const agentErrors: Array<DataPeriod> = [];
        const agent = agents.find((a) => a.id === agentId);

        agentStats.forEach((statPeriod, index) => {
          const startMoment = statPeriod.start?.moment_id;
          const endMoment = statPeriod.end?.moment_id;
          const startTime = util.formatTimestamp(statPeriod.start?.time);
          const endTime = util.formatTimestamp(statPeriod.end?.time);
          const startTimeStamp = sessionStats.buckets?.[index]?.start;
          const endTimeStamp = sessionStats.buckets?.[index]?.end;

          agentData.push({
            x: index,
            y: statPeriod.moments
              ? statPeriod.moments - (statPeriod.total_errors ?? 0)
              : 0,
            start: {
              moment: startMoment,
              time: startTime,
              timeStamp: startTimeStamp,
            },
            end: {
              moment: endMoment,
              time: endTime,
              timeStamp: endTimeStamp,
            },
            agentId,
            isError: false,
          });

          agentErrors.push({
            x: index,
            y: statPeriod.total_errors ?? 0,
            start: {
              moment: startMoment,
              time: startTime,
              timeStamp: startTimeStamp,
            },
            end: {
              moment: endMoment,
              time: endTime,
              timeStamp: endTimeStamp,
            },
            agentId,
            isError: true,
          });
        });

        data.datasets.push({
          label: getFriendlyAgentName(agent) ?? agentId,
          data: agentData,
          barPercentage: 1.2,
          ...getColorsForDataset(agentIndex, isDarkMode),
        });

        if (agentErrors.some((error) => error.y > 0)) {
          data.datasets.push({
            label: `Error - ${getFriendlyAgentName(agent) ?? agentId}`,
            data: agentErrors,
            barPercentage: 1.2,
            ...getErrorColors(),
          });
        }
      }
    );

    data.labels =
      sessionStats.buckets?.map((period) => {
        return util.formatTimestamp(period.start);
      }) ?? [];
  }

  return data;
};

export const getFunctionNames = (
  sessionStats?: SessionStats,
  agentId?: string,
  errors?: boolean
): Array<string> => {
  const functionNames: Set<string> = new Set();
  Object.entries(sessionStats?.agent_stats ?? {}).forEach(
    ([id, agentStats]) => {
      if (agentId && agentId !== id) {
        return;
      }

      agentStats.forEach((statPeriod) => {
        if (statPeriod.function_names && !errors) {
          Object.keys(statPeriod.function_names).forEach((functionName) => {
            functionNames.add(functionName);
          });
        }
        if (statPeriod.function_errors && errors) {
          Object.keys(statPeriod.function_errors).forEach((functionName) => {
            functionNames.add(functionName);
          });
        }
      });
    }
  );

  return Array.from(functionNames);
};

export const getSessionHistogramDataByFunction = ({
  agentId,
  sessionStats,
  selectedFunctionNames,
  errorsOnly,
  isDarkMode,
}: {
  agentId: string;
  sessionStats?: SessionStats;
  selectedFunctionNames?: Array<string>;
  errorsOnly?: boolean;
  isDarkMode: boolean;
}) => {
  const data: Data = {
    labels: [],
    datasets: [],
  };

  if (
    sessionStats?.agent_stats &&
    sessionStats.end?.time &&
    sessionStats.start?.time
  ) {
    let functionNames = errorsOnly
      ? []
      : getFunctionNames(sessionStats, agentId, false);
    const allErrorFunctionNames = getFunctionNames(sessionStats, agentId, true);
    functionNames =
      selectedFunctionNames && selectedFunctionNames?.length !== 0
        ? functionNames.filter((functionName) =>
            selectedFunctionNames?.includes(functionName)
          )
        : functionNames;
    const errorFunctionNames =
      selectedFunctionNames && selectedFunctionNames?.length !== 0
        ? allErrorFunctionNames.filter((functionName) =>
            selectedFunctionNames?.includes(functionName)
          )
        : allErrorFunctionNames;

    Object.entries(sessionStats.agent_stats).forEach(([id, agentStats]) => {
      if (id !== agentId) {
        return;
      }

      const dataPeriodMap: Record<string, Array<DataPeriod>> = {};
      const errorPeriodMap: Record<string, Array<DataPeriod>> = {};
      const errors: Array<DataPeriod> = [];

      functionNames.forEach((functionName) => {
        dataPeriodMap[functionName] = [];
      });

      errorFunctionNames.forEach((functionName) => {
        errorPeriodMap[functionName] = [];
      });

      agentStats.forEach((statPeriod, index) => {
        if (!statPeriod.function_names) {
          return;
        }

        const startMoment = statPeriod.start?.moment_id;
        const endMoment = statPeriod.end?.moment_id;
        const startTime = util.formatTimestamp(statPeriod.start?.time);
        const endTime = util.formatTimestamp(statPeriod.end?.time);
        const startTimeStamp = sessionStats.buckets?.[index]?.start;
        const endTimeStamp = sessionStats.buckets?.[index]?.end;

        let errorFunctionCount = 0;

        functionNames.forEach((functionName) => {
          dataPeriodMap[functionName]?.push({
            x: index,
            y: statPeriod?.function_names?.[functionName] ?? 0,
            start: {
              moment: startMoment,
              time: startTime,
              timeStamp: startTimeStamp,
            },
            end: {
              moment: endMoment,
              time: endTime,
              timeStamp: endTimeStamp,
            },
            agentId,
            functionName,
            isError: false,
          });
        });

        errorFunctionNames.forEach((functionName) => {
          errorPeriodMap[functionName]?.push({
            x: index,
            y: statPeriod?.function_errors?.[functionName] ?? 0,
            start: {
              moment: startMoment,
              time: startTime,
              timeStamp: startTimeStamp,
            },
            end: {
              moment: endMoment,
              time: endTime,
              timeStamp: endTimeStamp,
            },
            agentId,
            functionName,
            isError: true,
          });
        });

        if (selectedFunctionNames && selectedFunctionNames?.length === 0) {
          // Hide non-function-specific errors if we are filtering by function names
          allErrorFunctionNames.forEach((functionName) => {
            errorFunctionCount +=
              statPeriod?.function_errors?.[functionName] ?? 0;
          });

          errors.push({
            x: index,
            y: statPeriod.total_errors
              ? statPeriod.total_errors - errorFunctionCount
              : 0,
            start: {
              moment: startMoment,
              time: startTime,
              timeStamp: startTimeStamp,
            },
            end: {
              moment: endMoment,
              time: endTime,
              timeStamp: endTimeStamp,
            },
            agentId,
            isError: true,
          });
        }
      });

      Object.entries(dataPeriodMap).forEach(
        ([functionName, dataPeriods], dataIndex) => {
          data.datasets.push({
            label: `${functionName}`,
            data: dataPeriods,
            barPercentage: 1.2,
            ...getColorsForDataset(dataIndex, isDarkMode),
          });
        }
      );

      Object.entries(errorPeriodMap).forEach(([functionName, dataPeriods]) => {
        data.datasets.push({
          label: `${functionName} - Error`,
          data: dataPeriods,
          barPercentage: 1.2,
          ...getErrorColors(),
        });
      });

      if (errors.some((error) => error.y > 0)) {
        data.datasets.push({
          label: "Error",
          data: errors,
          barPercentage: 1.2,
          ...getErrorColors(),
        });
      }
    });

    data.labels =
      sessionStats.buckets?.map((period) => {
        return util.formatTimestamp(period.start);
      }) ?? [];
  }

  return data;
};
